import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import DownloadIcon from "@mui/icons-material/Download";
import { useNavigate, useParams } from "react-router-dom";
import ApiCallService from "../../../Service/ApiCallService";
import VibrancyStatusChip from "./VibrancyStatusChip";

const formatDate = (value) => {
  if (!value) return "N/A";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "N/A" : date.toLocaleDateString("en-IN");
};

const getLocationName = (chapter, key) => {
  const value = chapter?.location?.[key];
  return value?.[`${key}Name`] || value?.name || "";
};

const isPreviewable = (attachment) =>
  String(attachment?.mimeType || "").startsWith("image/") ||
  String(attachment?.mimeType || "") === "application/pdf" ||
  String(attachment?.mimeType || "") === "video/mp4";

const AttachmentPreview = ({ attachment }) => {
  if (String(attachment?.mimeType || "").startsWith("image/")) {
    return (
      <Box
        component="img"
        src={attachment.url}
        alt={attachment.originalName}
        sx={{ width: "100%", height: 160, objectFit: "cover", borderRadius: 1 }}
      />
    );
  }

  if (attachment?.mimeType === "video/mp4") {
    return (
      <Box component="video" src={attachment.url} controls sx={{ width: "100%", height: 160, borderRadius: 1 }} />
    );
  }

  if (attachment?.mimeType === "application/pdf") {
    return (
      <Box
        component="iframe"
        src={attachment.url}
        title={attachment.originalName}
        sx={{ width: "100%", height: 160, border: 0, borderRadius: 1 }}
      />
    );
  }

  return (
    <Typography variant="body2" color="text.secondary">
      Preview unavailable for this file type.
    </Typography>
  );
};

const VibrancySubmissionDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [submission, setSubmission] = useState(null);
  const [remarks, setRemarks] = useState("");
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [confirmApproveOpen, setConfirmApproveOpen] = useState(false);
  const [error, setError] = useState("");

  const loadSubmission = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const response = await ApiCallService.get(
        `${process.env.REACT_APP_BASE_URL}/vibrancy-submissions/${id}`,
      );
      setSubmission(response?.data || null);
      setRemarks(response?.data?.remarks || "");
    } catch (err) {
      console.error("Failed to load vibrancy submission:", err);
      setError("Failed to load submission details.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadSubmission();
  }, [loadSubmission]);

  const handleDecision = async (decision) => {
    if (decision === "reject" && !remarks.trim()) {
      setError("Remarks are required when rejecting a submission.");
      return;
    }

    try {
      setActionLoading(true);
      setError("");
      await ApiCallService.patch(
        `${process.env.REACT_APP_BASE_URL}/vibrancy-submissions/${id}/${decision}`,
        { remarks },
      );
      await loadSubmission();
      setConfirmApproveOpen(false);
    } catch (err) {
      console.error(`Failed to ${decision} submission:`, err);
      setError(err?.response?.data?.message || `Failed to ${decision} submission.`);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!submission) {
    return (
      <Box sx={{ p: 3 }}>
        {error && <Alert severity="error">{error}</Alert>}
      </Box>
    );
  }

  const chapter = submission.chapter || {};
  const canDecide = submission.status === "Pending";

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: "#f7f5fb", minHeight: "100vh" }}>
      <Stack spacing={3}>
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2}>
          <Box>
            <Typography variant="h4" fontWeight={900} color="#3f2367">
              Vibrancy Submission Details
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {chapter.chapterName}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <VibrancyStatusChip status={submission.status} />
            <Button variant="outlined" onClick={() => navigate("/vibrancy-admin")}>
              Back
            </Button>
          </Stack>
        </Stack>

        {error && <Alert severity="error">{error}</Alert>}

        <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" } }}>
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h6" fontWeight={800} gutterBottom>
              Chapter Information
            </Typography>
            <Stack spacing={1}>
              <Typography><strong>Chapter:</strong> {chapter.chapterName || "N/A"}</Typography>
              <Typography><strong>Code:</strong> {chapter.chapterUniqueCode || "N/A"}</Typography>
              <Typography>
                <strong>District / State:</strong>{" "}
                {[getLocationName(chapter, "district"), getLocationName(chapter, "state")].filter(Boolean).join(", ") || "N/A"}
              </Typography>
              <Typography><strong>Current Status:</strong> {chapter.chapterLifecycleStatus || "Prospective"}</Typography>
            </Stack>
          </Paper>

          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Typography variant="h6" fontWeight={800} gutterBottom>
              Program Details
            </Typography>
            <Stack spacing={1}>
              <Typography><strong>Program:</strong> {submission.foundationProgramName || submission.foundationProgram?.name || "N/A"}</Typography>
              <Typography><strong>Date:</strong> {formatDate(submission.programDate)}</Typography>
              <Typography><strong>Location:</strong> {submission.location}</Typography>
              <Typography><strong>Submitted:</strong> {formatDate(submission.submittedAt)}</Typography>
            </Stack>
          </Paper>
        </Box>

        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="h6" fontWeight={800} gutterBottom>
            Description
          </Typography>
          <Typography color="text.secondary" sx={{ whiteSpace: "pre-wrap" }}>
            {submission.description}
          </Typography>
        </Paper>

        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="h6" fontWeight={800} gutterBottom>
            Attachments
          </Typography>
          {submission.attachments?.length ? (
            <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" } }}>
              {submission.attachments.map((attachment) => (
                <Card key={attachment.url} variant="outlined" sx={{ borderRadius: 2 }}>
                  <CardContent>
                    {isPreviewable(attachment) && <AttachmentPreview attachment={attachment} />}
                    <Typography variant="subtitle2" fontWeight={800} sx={{ mt: 1 }} noWrap title={attachment.originalName}>
                      {attachment.originalName}
                    </Typography>
                    <Button
                      component="a"
                      href={attachment.url}
                      target="_blank"
                      rel="noopener"
                      startIcon={<DownloadIcon />}
                      sx={{ mt: 1 }}
                    >
                      Download
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </Box>
          ) : (
            <Typography color="text.secondary">No attachments uploaded.</Typography>
          )}
        </Paper>

        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Typography variant="h6" fontWeight={800} gutterBottom>
            Review
          </Typography>
          <TextField
            label="Remarks"
            value={remarks}
            onChange={(event) => setRemarks(event.target.value)}
            multiline
            minRows={3}
            fullWidth
            disabled={!canDecide}
          />
          <Divider sx={{ my: 2 }} />
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="flex-end">
            <Button
              variant="outlined"
              color="error"
              startIcon={<CancelIcon />}
              disabled={!canDecide || actionLoading}
              onClick={() => handleDecision("reject")}
            >
              Reject
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<CheckCircleIcon />}
              disabled={!canDecide || actionLoading}
              onClick={() => setConfirmApproveOpen(true)}
            >
              Approve
            </Button>
          </Stack>
        </Paper>
      </Stack>

      <Dialog open={confirmApproveOpen} onClose={() => setConfirmApproveOpen(false)}>
        <DialogTitle>Confirm Approval</DialogTitle>
        <DialogContent>
          <Typography>
            Approving this submission will change the chapter lifecycle from Prospective to Vibrant. Continue?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmApproveOpen(false)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={() => handleDecision("approve")}
            disabled={actionLoading}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VibrancySubmissionDetails;
